import React from "react";
import styles from "./InventoryColumn.module.css";
import { QuantityBadge } from "../../../components/QuantityBadge";
import { ResultEntry } from "../../../types/data";
import { EanQuantityBundle } from "../../../components/EanQuantityBundle";
import { EANView } from "../../../components/EANView";

interface Props {
  title: string;
  databaseItems: ResultEntry[];
  newItems: ResultEntry[];
  onItemClick: (ean: string) => void;
  onEditItem: (ean: string, currentQty: number) => void;
  children?: React.ReactNode;
  isLoading?: boolean;
}

export const InventoryColumn = ({
  title,
  databaseItems,
  newItems,
  onItemClick,
  onEditItem,
  children,
  isLoading = false
}: Props) => {
  const skeletonRows = Array.from({ length: 2 }, (_, i) => i);

  const allEans = Array.from(new Set([...databaseItems.map((i) => i.ean), ...newItems.map((i) => i.ean)])).sort(
    (a, b) => Number(a) - Number(b)
  );

  const hasItems = allEans.length > 0;
  const shouldShowContainer = isLoading || hasItems;

  return (
    <div className={styles.column}>
      <div className={styles.title}>{title}</div>
      {shouldShowContainer ? (
        <div className={styles.bundleContainer}>
          {isLoading
            ? skeletonRows.map((key) => (
                <EanQuantityBundle isLoading key={key}>
                  <span>0000</span>
                  <QuantityBadge>{0}</QuantityBadge>
                </EanQuantityBundle>
              ))
            : allEans.map((ean) => {
                const dbItem = databaseItems.find((o) => o.ean === ean);
                const newItem = newItems.find((o) => o.ean === ean);

                const oldQty = dbItem ? dbItem.quantity : 0;
                const newQty = newItem ? newItem.quantity : 0;

                let borderStatus = "";
                let badgeStatus: "plus" | "minus" | "neutral" = "neutral";

                if (dbItem && !newItem) {
                  borderStatus = styles.removed;
                  badgeStatus = "minus";
                } else if (!dbItem && newItem) {
                  borderStatus = styles.added;
                  badgeStatus = "plus";
                } else if (newQty > oldQty) {
                  badgeStatus = "plus";
                } else if (newQty < oldQty) {
                  badgeStatus = "minus";
                }

                return (
                  <EanQuantityBundle className={`${styles.bundle} ${borderStatus}`} key={ean}>
                    <div className={styles.toggleArea} onClick={() => onItemClick(ean)}>
                      <EANView ean={ean} />
                    </div>
                    <div
                      className={styles.editArea}
                      onClick={() => onEditItem(ean, oldQty)}
                      title="Kliknij, aby edytować ilość"
                    >
                      {badgeStatus !== "neutral" && (
                        <>
                          {!(!dbItem && newItem) && <QuantityBadge status={badgeStatus}>{oldQty}</QuantityBadge>}
                          {!((dbItem && !newItem) || (!dbItem && newItem)) && <span className={styles.arrow}>-&gt;</span>}
                        </>
                      )}
                      {!(dbItem && !newItem) && <QuantityBadge status={badgeStatus}>{newQty}</QuantityBadge>}
                    </div>
                  </EanQuantityBundle>
                );
              })}
        </div>
      ) : (
        <>Brak</>
      )}
      {children}
    </div>
  );
};
